// Official company-letterhead print engine for the Reports & Analytics hub.
// Every report is rendered as a standalone A4 document in a new window so the
// printout is never affected by the app layout (sidebar, tabs, buttons, colors).

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

export const money = (v) => Math.round(num(v)).toLocaleString('en-US');
const signed = (v) => (num(v) > 0 ? '+' : num(v) < 0 ? '-' : '') + money(Math.abs(num(v)));
const pct = (v) => `${num(v).toFixed(1)}%`;
const dateGB = (d) => {
  if (!d) return '—';
  const dt = new Date(String(d).length <= 10 ? `${d}T12:00:00` : d);
  return isNaN(dt) ? esc(d) : dt.toLocaleDateString('en-GB');
};

// ─── Building blocks ─────────────────────────────────────────────────────────

const buildCompany = (s = {}) => ({
  name: s.company_name || 'SUGIMCO',
  subtitle: s.company_subtitle || 'SUPER MARCHE Yazbek',
  logo: s.company_logo || '',
  nif: s.company_nif || '',
  rc: s.company_rc || '',
  phone: s.company_phone || '',
  phone2: s.company_phone2 || '',
  address: s.company_address || '',
  email: s.company_email || '',
});

const css = (orientation) => `
  @page {
    size: A4 ${orientation};
    margin: 12mm 11mm 16mm 11mm;
    @bottom-right { content: "Page " counter(page) " / " counter(pages); font: 8pt Arial, sans-serif; color: #555; }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body {
    font-family: 'Segoe UI', Arial, Helvetica, sans-serif;
    font-size: 9.5pt; color: #111; line-height: 1.35;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .sheet { margin: 0 auto; max-width: ${orientation === 'landscape' ? '275mm' : '188mm'}; }
  @media screen {
    body { background: #e5e7eb; padding: 20px 0; }
    .sheet { background: #fff; padding: 12mm 11mm; box-shadow: 0 2px 12px rgba(0,0,0,.15); }
  }

  .lh { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 8px; }
  .lh-left { display: flex; align-items: center; gap: 12px; }
  .lh-left img { max-height: 58px; max-width: 120px; object-fit: contain; }
  .co-name { font-size: 19pt; font-weight: 800; letter-spacing: .5px; text-transform: uppercase; color: #0b2545; margin: 0; line-height: 1.05; }
  .co-sub { font-size: 8.5pt; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #444; margin-top: 3px; }
  .lh-right { text-align: right; font-size: 8pt; color: #333; line-height: 1.5; }
  .rule { height: 3px; background: #0b2545; border-bottom: 1px solid #c9a227; margin-bottom: 12px; }

  .doc-head { display: flex; justify-content: space-between; align-items: stretch; gap: 14px; margin-bottom: 6px; }
  .doc-title { flex: 1; }
  .doc-title h1 { margin: 0; font-size: 14.5pt; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #0b2545; }
  .doc-title p { margin: 4px 0 0; font-size: 8.5pt; color: #444; }
  .meta { border: 1px solid #0b2545; min-width: 70mm; font-size: 8pt; border-collapse: collapse; }
  .meta td { padding: 3px 8px; border-bottom: 1px solid #d4d8de; }
  .meta td:first-child { font-weight: 700; text-transform: uppercase; color: #0b2545; background: #f1f4f8; width: 40%; }
  .meta tr:last-child td { border-bottom: 0; }

  h2.sec { font-size: 9.5pt; text-transform: uppercase; letter-spacing: 1px; color: #fff; background: #0b2545; padding: 5px 8px; margin: 14px 0 0; page-break-after: avoid; }
  h3.sub { font-size: 8.5pt; text-transform: uppercase; letter-spacing: .6px; color: #0b2545; margin: 10px 0 4px; border-bottom: 1.5px solid #c9a227; padding-bottom: 2px; }

  table.grid { width: 100%; border-collapse: collapse; font-size: 8.5pt; }
  table.grid th { background: #f1f4f8; color: #0b2545; font-size: 7.5pt; text-transform: uppercase; letter-spacing: .4px; padding: 5px 6px; border: 1px solid #b8c0cc; text-align: left; }
  table.grid td { padding: 4px 6px; border: 1px solid #d4d8de; vertical-align: top; }
  table.grid tbody tr:nth-child(even) td { background: #fafbfc; }
  table.grid tfoot td { font-weight: 800; background: #e8edf3; border-top: 1.5px solid #0b2545; }
  table.grid tr.muted td { color: #9aa3ad; }
  table.grid tr.grand td { font-weight: 800; background: #0b2545 !important; color: #fff; border-color: #0b2545; }
  table.grid tr.sub td { font-weight: 800; background: #f1f4f8 !important; }
  .r { text-align: right; } .c { text-align: center; }
  .n { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .pos { color: #166534; } .neg { color: #b91c1c; }
  .note { font-size: 7pt; color: #666; margin-top: 1px; }
  .empty { text-align: center; color: #888; font-style: italic; padding: 10px; }

  .kf { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #b8c0cc; border-top: 0; }
  .kf div { padding: 6px 8px; border-right: 1px solid #d4d8de; border-top: 1px solid #d4d8de; }
  .kf div:nth-child(4n) { border-right: 0; }
  .kf span { display: block; font-size: 6.8pt; text-transform: uppercase; letter-spacing: .5px; color: #555; font-weight: 700; }
  .kf b { display: block; font-size: 10.5pt; margin-top: 2px; font-variant-numeric: tabular-nums; }

  .two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 7pt; font-weight: 800; text-transform: uppercase; letter-spacing: .4px; border: 1px solid; }
  .b-ok { color: #166534; border-color: #86efac; background: #f0fdf4; }
  .b-warn { color: #92400e; border-color: #fcd34d; background: #fffbeb; }
  .b-bad { color: #991b1b; border-color: #fca5a5; background: #fef2f2; }
  .b-info { color: #1e3a8a; border-color: #93c5fd; background: #eff6ff; }

  .box { border: 1px solid #b8c0cc; padding: 8px 10px; margin-top: 8px; font-size: 8.5pt; }
  .declaration { margin-top: 12px; font-size: 8pt; color: #333; border-left: 3px solid #c9a227; padding: 6px 10px; background: #fbf8ee; }

  .signs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 18px; page-break-inside: avoid; }
  .sign { border: 1px solid #b8c0cc; height: 26mm; padding: 6px 8px; display: flex; flex-direction: column; justify-content: space-between; }
  .sign .t { font-size: 7.5pt; font-weight: 800; text-transform: uppercase; color: #0b2545; }
  .sign .l { border-top: 1px dotted #666; font-size: 7pt; color: #666; padding-top: 3px; }

  .footer { margin-top: 14px; text-align: center; font-size: 7pt; color: #555; border-top: 1px solid #b8c0cc; padding-top: 4px; }

  tr { page-break-inside: avoid; }
  thead { display: table-header-group; }
  tfoot { display: table-row-group; }
  .avoid { page-break-inside: avoid; }
`;

const letterhead = (c) => {
  const contact = [
    c.address && esc(c.address),
    [c.phone && `Tel: ${esc(c.phone)}`, c.phone2 && esc(c.phone2)].filter(Boolean).join(' / '),
    c.email && esc(c.email),
    [c.nif && `NIF: ${esc(c.nif)}`, c.rc && `RC: ${esc(c.rc)}`].filter(Boolean).join(' &nbsp;|&nbsp; '),
  ].filter(Boolean);
  return `
  <header class="lh">
    <div class="lh-left">
      ${c.logo ? `<img src="${esc(c.logo)}" alt="Logo">` : ''}
      <div><p class="co-name">${esc(c.name)}</p><div class="co-sub">${esc(c.subtitle)}</div></div>
    </div>
    <div class="lh-right">${contact.join('<br>')}</div>
  </header>
  <div class="rule"></div>`;
};

const docHead = (title, subtitle, meta) => `
  <section class="doc-head">
    <div class="doc-title"><h1>${esc(title)}</h1>${subtitle ? `<p>${subtitle}</p>` : ''}</div>
    <table class="meta">${meta.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${v}</td></tr>`).join('')}</table>
  </section>`;

const keyFigures = (items) => {
  const padded = [...items];
  while (padded.length % 4 !== 0) padded.push(null);
  return `<div class="kf">${padded
    .map((i) => (i ? `<div><span>${esc(i.label)}</span><b class="${i.tone || ''}">${i.value}</b></div>` : '<div></div>'))
    .join('')}</div>`;
};

/**
 * columns: [{ label, align: 'r'|'c'|'', num: bool }]
 * rows: array of arrays (already-escaped HTML strings) or { cells, cls }
 */
const table = (columns, rows, { foot, empty = 'No records.' } = {}) => {
  const cls = (c) => [c.align, c.num ? 'n' : ''].filter(Boolean).join(' ');
  const body = rows.length
    ? rows
        .map((r) => {
          const cells = Array.isArray(r) ? r : r.cells;
          const rc = Array.isArray(r) ? '' : r.cls || '';
          return `<tr class="${rc}">${cells.map((v, i) => `<td class="${cls(columns[i] || {})}">${v}</td>`).join('')}</tr>`;
        })
        .join('')
    : `<tr><td class="empty" colspan="${columns.length}">${esc(empty)}</td></tr>`;
  const tfoot = foot
    ? `<tfoot><tr>${foot.map((v, i) => `<td class="${cls(columns[i] || {})}">${v}</td>`).join('')}</tr></tfoot>`
    : '';
  return `<table class="grid"><thead><tr>${columns
    .map((c) => `<th class="${c.align || ''}">${esc(c.label)}</th>`)
    .join('')}</tr></thead><tbody>${body}</tbody>${tfoot}</table>`;
};

const signatures = (labels) =>
  `<div class="signs">${labels
    .map(([t, l]) => `<div class="sign"><span class="t">${esc(t)}</span><span class="l">${l ? esc(l) : 'Name, signature &amp; date'}</span></div>`)
    .join('')}</div>`;

const toneOf = (v) => (num(v) >= 0 ? 'pos' : 'neg');

function openPrint({ settings, title, orientation = 'portrait', reference, body }) {
  const company = buildCompany(settings);
  const now = new Date();
  const printed = `${now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${esc(company.name)} - ${esc(title)}</title>
<style>${css(orientation)}</style>
</head>
<body>
<div class="sheet">
  ${letterhead(company)}
  ${body(printed)}
  <div class="footer">
    ${esc(company.name)}${company.address ? ' &bull; ' + esc(company.address) : ''}${company.nif ? ' &bull; NIF ' + esc(company.nif) : ''}${company.rc ? ' &bull; RC ' + esc(company.rc) : ''}
    &bull; Ref ${esc(reference)} &bull; Printed ${printed} &bull; Generated by ERP system
  </div>
</div>
<script>
  (function () {
    var imgs = Array.prototype.slice.call(document.images);
    var pending = imgs.filter(function (i) { return !i.complete; });
    function go() { setTimeout(function () { window.focus(); window.print(); }, 200); }
    window.onafterprint = function () { window.close(); };
    if (!pending.length) return go();
    var left = pending.length;
    pending.forEach(function (i) { i.onload = i.onerror = function () { if (--left === 0) go(); }; });
  })();
</script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=1100,height=850,scrollbars=yes');
  if (!win) {
    alert('Please allow pop-ups for this site to print the report.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

const ref = (prefix) => `${prefix}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(Date.now()).slice(-4)}`;

// ═════════════════════════════════════════════════════════════════════════════
// 1. BUSINESS PERFORMANCE REPORT  (Tab: Performance Overview)
// ═════════════════════════════════════════════════════════════════════════════
export function printPerformanceReport({ settings, preparedBy, periodLabel, periodRange, branchLabel, mba, bi, analytics }) {
  const ex = mba?.executive_summary || {};
  const cr = mba?.cost_recovery || {};
  const bb = mba?.branch_breakdown || [];
  const perf = bi?.performance;
  const be = analytics?.breakeven;
  const reference = ref('BPR');

  const body = () => `
  ${docHead('Business Performance Report',
    `Consolidated profitability, inventory valuation &amp; branch performance for <b>${esc(periodRange)}</b>.`,
    [['Reference', reference], ['Period', esc(periodLabel)], ['Branch', esc(branchLabel)], ['Currency', 'XAF (CFA Franc)']])}

  <h2 class="sec">1. Key Figures</h2>
  ${keyFigures([
    { label: 'Sales Revenue', value: money(ex.sales_revenue) },
    { label: 'Cost of Goods Sold', value: money(ex.cogs) },
    { label: 'Gross Profit', value: money(ex.gross_profit), tone: toneOf(ex.gross_profit) },
    { label: 'Operating Expenses', value: money(ex.operating_expenses) },
    { label: 'Net Profit / Loss', value: signed(ex.net_profit), tone: toneOf(ex.net_profit) },
    { label: 'Net Margin', value: pct(ex.profit_margin), tone: toneOf(ex.profit_margin) },
    { label: 'Stock at Cost', value: money(ex.inventory_cost) },
    { label: 'Stock at Retail', value: money(ex.inventory_retail) },
  ])}

  <h2 class="sec">2. Profit &amp; Loss Statement</h2>
  ${table(
    [{ label: 'Ref', align: 'c' }, { label: 'Description' }, { label: 'Amount (CFA)', align: 'r', num: true }],
    [
      ['A1', 'Sales revenue<div class="note">Total invoiced sales for the period</div>', money(ex.sales_revenue)],
      ['A2', 'Cost of goods sold (COGS)<div class="note">Purchase cost of the items sold</div>', `-${money(ex.cogs)}`],
      { cells: ['A3', 'GROSS PROFIT', money(ex.gross_profit)], cls: 'sub' },
      ['B1', 'Operating expenses<div class="note">Rent, salaries, utilities and other depenses</div>', `-${money(ex.operating_expenses)}`],
      { cells: ['B2', `NET PROFIT / LOSS &nbsp;<span class="note" style="color:#cfd8e3">margin ${pct(ex.profit_margin)}</span>`, signed(ex.net_profit)], cls: 'grand' },
    ]
  )}

  <div class="two avoid">
    <div>
      <h3 class="sub">Inventory Valuation</h3>
      ${table(
        [{ label: 'Item' }, { label: 'CFA', align: 'r', num: true }],
        [
          ['Stock at purchase cost', money(ex.inventory_cost)],
          ['Stock at retail value', money(ex.inventory_retail)],
          { cells: ['Potential margin in stock', money(ex.inventory_potential_profit)], cls: 'sub' },
        ]
      )}
    </div>
    <div>
      <h3 class="sub">Cost Recovery</h3>
      ${table(
        [{ label: 'Item' }, { label: 'CFA', align: 'r', num: true }],
        [
          ['Total inventory investment', money(cr.initial_investment)],
          ['Recovered from sales (COGS)', money(cr.recovered_from_sales)],
          ['Remaining to recover', money(cr.remaining_to_recover)],
          { cells: [`Recovery rate &nbsp;<span class="badge ${cr.fully_recovered ? 'b-ok' : 'b-warn'}">${cr.fully_recovered ? 'Recovered' : 'In progress'}</span>`, pct(cr.recovery_percentage)], cls: 'sub' },
        ]
      )}
    </div>
  </div>

  <h2 class="sec">3. Branch Performance</h2>
  ${table(
    [
      { label: '#', align: 'c' }, { label: 'Branch' },
      { label: 'Stock Cost', align: 'r', num: true }, { label: 'Retail Value', align: 'r', num: true },
      { label: 'Sales', align: 'r', num: true }, { label: 'COGS', align: 'r', num: true },
      { label: 'Expenses', align: 'r', num: true }, { label: 'Net Profit', align: 'r', num: true },
    ],
    bb.map((b, i) => [
      i + 1, esc(b.branch_name), money(b.stock_cost), money(b.stock_retail), money(b.sales),
      money(b.cogs), money(b.expenses), `<span class="${toneOf(b.net_profit)}">${signed(b.net_profit)}</span>`,
    ]),
    {
      foot: ['', 'NETWORK TOTAL', money(ex.inventory_cost), money(ex.inventory_retail), money(ex.sales_revenue), money(ex.cogs), money(ex.operating_expenses), signed(ex.net_profit)],
      empty: 'No branch data for this period.',
    }
  )}

  ${perf || be ? `
  <div class="two avoid">
    ${perf ? `<div>
      <h3 class="sub">Profit Windows (all branches)</h3>
      ${table(
        [{ label: 'Window' }, { label: 'Revenue', align: 'r', num: true }, { label: 'Profit', align: 'r', num: true }],
        [
          ['Today', money(perf.today?.revenue), signed(perf.today?.profit)],
          ['Month to date', money(perf.month?.revenue), signed(perf.month?.profit)],
          ['Year to date', money(perf.year?.revenue), signed(perf.year?.profit)],
        ]
      )}
    </div>` : '<div></div>'}
    ${be ? `<div>
      <h3 class="sub">30-Day Break-Even Analysis</h3>
      ${table(
        [{ label: 'Item' }, { label: 'Value', align: 'r', num: true }],
        [
          ['Fixed overheads', money(be.fixed_costs)],
          ['Average gross margin', pct(be.gross_margin_pct)],
          ['Required revenue (break-even)', money(be.break_even_point)],
          ['Current 30-day revenue', money(be.current_revenue_30d)],
          { cells: ['Status', `<span class="badge ${be.status === 'Profitable' ? 'b-ok' : 'b-warn'}">${esc(be.status)}</span>`], cls: 'sub' },
        ]
      )}
    </div>` : '<div></div>'}
  </div>` : ''}

  ${bi ? `
  <h2 class="sec">4. Product Insights</h2>
  <div class="two">
    <div>
      <h3 class="sub">Top Profit Contributors</h3>
      ${table(
        [{ label: '#', align: 'c' }, { label: 'Product' }, { label: 'Profit (CFA)', align: 'r', num: true }],
        (bi.top_contributors || []).slice(0, 10).map((p, i) => [i + 1, esc(p.name), money(p.profit_contribution)]),
        { empty: 'No sales recorded.' }
      )}
    </div>
    <div>
      <h3 class="sub">Slow-Moving Inventory</h3>
      ${table(
        [{ label: '#', align: 'c' }, { label: 'Product' }, { label: 'Stock', align: 'r', num: true }, { label: 'Value (CFA)', align: 'r', num: true }],
        (bi.loss_analysis?.slow_movers || []).slice(0, 10).map((p, i) => [i + 1, esc(p.name), money(p.stock), money(p.value)]),
        { foot: ['', 'Capital tied in slow stock', '', money(bi.loss_analysis?.total_slow_value)], empty: 'No dormant stock.' }
      )}
    </div>
  </div>` : ''}

  <div class="declaration avoid">
    <b>Management conclusion.</b> For the period <b>${esc(periodRange)}</b> the business${branchLabel !== 'All Branches' ? ` (branch: <b>${esc(branchLabel)}</b>)` : ''}
    recorded a <b>${num(ex.net_profit) >= 0 ? 'net profit' : 'net loss'}</b> of <b>${money(Math.abs(num(ex.net_profit)))} CFA</b>
    on sales of <b>${money(ex.sales_revenue)} CFA</b> (net margin ${pct(ex.profit_margin)}).
    Inventory investment ${cr.fully_recovered ? 'has been <b>fully recovered</b>' : `is <b>${pct(cr.recovery_percentage)}</b> recovered, with <b>${money(cr.remaining_to_recover)} CFA</b> still to recover`}.
  </div>

  ${signatures([['Prepared by (Finance)', preparedBy], ['Reviewed by (Controller)'], ['Approved by (Management)']])}`;

  openPrint({ settings, title: `Business Performance Report ${periodLabel}`, reference, body });
}

// ═════════════════════════════════════════════════════════════════════════════
// 2. DAILY CLOSING REPORT (Z-REPORT)  (Tab: Daily & Monthly)
// ═════════════════════════════════════════════════════════════════════════════
export function printDailyClosing({ settings, preparedBy, branchName, date, status, summary = {}, expenses = [], purchases = [] }) {
  const s = summary || {};
  const isClosed = status === 'Closed';
  const cash = num(s.cash_total);
  const card = num(s.card_total);
  const credit = num(s.account_credit_total);
  const expTotal = num(s.expense_total);
  const achat = num(s.achat_total);
  const expected = s.expected_cash != null ? num(s.expected_cash) : cash - expTotal;
  const reference = ref('EOD');
  const cashiers = s.cashier_summary || [];
  const purchTotal = purchases.reduce((t, p) => t + num(p.grand_total || p.total_cost), 0);
  const expListTotal = expenses.reduce((t, e) => t + num(e.amount), 0);

  const body = () => `
  ${docHead('Daily Closing Report (Z-Report)',
    `End-of-day sales, cash reconciliation and outflows for <b>${esc(branchName)}</b> on <b>${dateGB(date)}</b>.`,
    [['Reference', reference], ['Business Date', dateGB(date)], ['Branch', esc(branchName)],
     ['Day Status', `<span class="badge ${isClosed ? 'b-bad' : 'b-ok'}">${isClosed ? 'Closed / Locked' : 'Open'}</span>`]])}

  <h2 class="sec">1. Key Figures</h2>
  ${keyFigures([
    { label: 'Gross Sales', value: money(s.gross_sales) },
    { label: 'Returns / Refunds', value: money(s.return_total) },
    { label: 'Net Sales', value: money(s.net_sales) },
    { label: 'Invoices Issued', value: money(s.invoice_count) },
    { label: 'Cash Collected', value: money(cash) },
    { label: 'Card / Visa', value: money(card) },
    { label: 'Credit (Accounts)', value: money(credit) },
    { label: 'Tax (TVA) Included', value: money(s.tax_total) },
  ])}

  <div class="two avoid">
    <div>
      <h3 class="sub">Collections by Payment Method</h3>
      ${table(
        [{ label: 'Method' }, { label: 'Amount (CFA)', align: 'r', num: true }],
        [
          ['Cash (all invoices)', money(cash)],
          ['&nbsp;&nbsp;of which walk-in POS cash', money(s.walkin_total)],
          ['Card / Visa', money(card)],
          ['Account credit (on account)', money(credit)],
        ],
        { foot: ['TOTAL', money(cash + card + credit)] }
      )}
    </div>
    <div>
      <h3 class="sub">Cash Drawer Reconciliation</h3>
      ${table(
        [{ label: 'Line' }, { label: 'Amount (CFA)', align: 'r', num: true }],
        [
          ['Cash collected', money(cash)],
          ['Less: expenses paid (depenses)', `-${money(expTotal)}`],
          { cells: ['EXPECTED CASH IN DRAWER', money(expected)], cls: 'sub' },
          ['Actual cash counted', isClosed ? money(s.actual_cash) : '________________'],
          { cells: ['Difference (actual - expected)', isClosed ? `<span class="${toneOf(s.cash_difference)}">${signed(s.cash_difference)}</span>` : '________________'], cls: 'sub' },
        ]
      )}
      ${isClosed && s.difference_reason ? `<div class="note" style="margin-top:3px">Reason: ${esc(s.difference_reason)}</div>` : ''}
    </div>
  </div>

  ${cashiers.length ? `
  <h2 class="sec">2. Cashier Breakdown</h2>
  ${table(
    [{ label: 'Cashier' }, { label: 'Invoices', align: 'r', num: true }, { label: 'Sales', align: 'r', num: true }, { label: 'Returns', align: 'r', num: true }, { label: 'Expenses', align: 'r', num: true }],
    cashiers.map((c) => [esc(c.name), money(c.invoice_count), money(c.total_sales), money(c.returns), money(c.expenses)]),
    { foot: ['TOTAL', money(cashiers.reduce((t, c) => t + num(c.invoice_count), 0)), money(cashiers.reduce((t, c) => t + num(c.total_sales), 0)), money(cashiers.reduce((t, c) => t + num(c.returns), 0)), money(cashiers.reduce((t, c) => t + num(c.expenses), 0))] }
  )}` : ''}

  <h2 class="sec">${cashiers.length ? '3' : '2'}. Outflows of the Day</h2>
  <h3 class="sub">Expenses (Depenses)</h3>
  ${table(
    [{ label: 'Ref' }, { label: 'Category' }, { label: 'Description' }, { label: 'Method' }, { label: 'Amount (CFA)', align: 'r', num: true }],
    expenses.map((e) => [esc(e.reference_number || `#${e.id}`), esc(e.category || '—'), esc(e.description || ''), esc(e.payment_method || 'Cash'), money(e.amount)]),
    { foot: ['', '', '', 'TOTAL', money(expListTotal || expTotal)], empty: 'No expenses recorded for this day.' }
  )}
  <h3 class="sub">Purchases (Achats)</h3>
  ${table(
    [{ label: 'ID' }, { label: 'Supplier' }, { label: 'Status' }, { label: 'Payment Source' }, { label: 'Amount (CFA)', align: 'r', num: true }],
    purchases.map((p) => [`#${esc(p.id)}`, esc(p.supplier || p.supplier_name || '—'), esc(p.status || '—'), esc(p.payment_source || '—'), money(p.grand_total || p.total_cost)]),
    { foot: ['', '', '', 'TOTAL', money(purchTotal || achat)], empty: 'No purchases recorded for this day.' }
  )}

  <div class="declaration avoid">
    I, the undersigned, certify that the cash, card and credit amounts above correspond to the sales recorded in the ERP system for
    <b>${esc(branchName)}</b> on <b>${dateGB(date)}</b>${isClosed ? ', and that the day has been closed and locked' : ''}.
  </div>

  ${signatures([['Cashier', ''], ['Branch Manager', preparedBy], ['Finance Controller', '']])}`;

  openPrint({ settings, title: `Daily Closing ${branchName} ${date}`, reference, body });
}

// ═════════════════════════════════════════════════════════════════════════════
// 3. MONTHLY SALES & OUTFLOW MATRIX  (Tab: Daily & Monthly) — landscape
// ═════════════════════════════════════════════════════════════════════════════
export function printMonthlyMatrix({ settings, preparedBy, branchName, monthLabel, rows = [], totals = {} }) {
  const reference = ref('MSM');
  const active = rows.filter((r) => num(r.total_daily_sales) > 0 || num(r.accepted_achats) > 0 || num(r.depenses) > 0);
  const closed = rows.filter((r) => r.day_status === 'Closed').length;
  const best = active.reduce((b, r) => (num(r.total_daily_sales) > num(b?.total_daily_sales) ? r : b), null);
  const avg = active.length ? num(totals.total_sales) / active.length : 0;

  const body = () => `
  ${docHead('Monthly Sales &amp; Outflow Matrix',
    `Day-by-day sales by payment method, purchases, expenses and net cash flow for <b>${esc(branchName)}</b>.`,
    [['Reference', reference], ['Month', esc(monthLabel)], ['Branch', esc(branchName)], ['Currency', 'XAF (CFA Franc)']])}

  <h2 class="sec">1. Month Summary</h2>
  ${keyFigures([
    { label: 'Total Sales', value: money(totals.total_sales) },
    { label: 'Cash Sales', value: money(totals.cash_sales) },
    { label: 'Card / Visa Sales', value: money(totals.visa_sales) },
    { label: 'Credit Sales', value: money(totals.credit_sales) },
    { label: 'Purchases (Achats)', value: money(totals.accepted_achats) },
    { label: 'Expenses (Depenses)', value: money(totals.depenses) },
    { label: 'Net Cash Flow', value: signed(totals.net_cash_flow), tone: toneOf(totals.net_cash_flow) },
    { label: 'Active / Closed Days', value: `${active.length} / ${closed}` },
  ])}

  <h2 class="sec">2. Daily Matrix</h2>
  ${table(
    [
      { label: 'Date' }, { label: 'Day' },
      { label: 'Cash', align: 'r', num: true }, { label: 'Card / Visa', align: 'r', num: true },
      { label: 'Credit', align: 'r', num: true }, { label: 'Total Sales', align: 'r', num: true },
      { label: 'Achats', align: 'r', num: true }, { label: 'Depenses', align: 'r', num: true },
      { label: 'Net Cash Flow', align: 'r', num: true }, { label: 'Status', align: 'c' },
    ],
    rows.map((r) => {
      const has = num(r.total_daily_sales) > 0 || num(r.accepted_achats) > 0 || num(r.depenses) > 0;
      const d = new Date(`${r.date}T12:00:00`);
      return {
        cls: has ? '' : 'muted',
        cells: [
          dateGB(r.date), d.toLocaleDateString('en-GB', { weekday: 'short' }),
          has ? money(r.cash_sales) : '—', has ? money(r.visa_sales) : '—', has ? money(r.credit_sales) : '—',
          has ? `<b>${money(r.total_daily_sales)}</b>` : '—', has ? money(r.accepted_achats) : '—', has ? money(r.depenses) : '—',
          has ? `<span class="${toneOf(r.net_daily_cash_flow)}">${signed(r.net_daily_cash_flow)}</span>` : '—',
          r.day_status ? `<span class="badge ${r.day_status === 'Closed' ? 'b-info' : 'b-ok'}">${esc(r.day_status)}</span>` : '',
        ],
      };
    }),
    { foot: ['MONTH TOTAL', '', money(totals.cash_sales), money(totals.visa_sales), money(totals.credit_sales), money(totals.total_sales), money(totals.accepted_achats), money(totals.depenses), signed(totals.net_cash_flow), ''] }
  )}

  <div class="box avoid">
    <b>Highlights:</b> average daily sales on active days <b>${money(avg)} CFA</b>${best ? `; best day <b>${dateGB(best.date)}</b> with <b>${money(best.total_daily_sales)} CFA</b>` : ''};
    ${closed} of ${rows.length} days closed. Net cash flow = (Cash + Card) − Depenses.
  </div>

  ${signatures([['Prepared by', preparedBy], ['Branch Manager'], ['Approved by (Management)']])}`;

  openPrint({ settings, title: `Monthly Matrix ${branchName} ${monthLabel}`, orientation: 'landscape', reference, body });
}

// ═════════════════════════════════════════════════════════════════════════════
// 4. FINANCIAL AUDIT & ALERTS REPORT  (Tab: Audit & Alerts)
// ═════════════════════════════════════════════════════════════════════════════
export function printFinancialAudit({ settings, preparedBy, startDate, endDate, fin, expiry = [], expiryDays, includeFinance = true }) {
  const reference = ref('FAR');
  const debit = num(fin?.totals?.debit);
  const credit = num(fin?.totals?.credit);
  const diff = debit - credit;
  const balanced = Math.abs(diff) < 0.01;
  const categories = Object.entries(fin?.categories || {});
  const journal = fin?.journal || [];
  const alerts = fin?.alerts || [];
  const urgency = (d) => (d <= 0 ? ['Expired', 'b-bad'] : d <= 7 ? ['Critical', 'b-bad'] : d <= 30 ? ['Soon', 'b-warn'] : ['Watch', 'b-info']);
  let n = 0;
  const sec = () => ++n;

  const body = () => `
  ${docHead(includeFinance ? 'Financial Audit &amp; Alerts Report' : 'Stock Expiry Alerts Report',
    includeFinance
      ? `Double-entry ledger audit for <b>${dateGB(startDate)}</b> to <b>${dateGB(endDate)}</b>, with stock expiry alerts.`
      : `Products expiring within the next <b>${esc(expiryDays)}</b> days.`,
    includeFinance
      ? [['Reference', reference], ['From', dateGB(startDate)], ['To', dateGB(endDate)],
         ['Ledger', `<span class="badge ${balanced ? 'b-ok' : 'b-bad'}">${balanced ? 'Balanced' : 'Out of balance'}</span>`]]
      : [['Reference', reference], ['Window', `${esc(expiryDays)} days`], ['Items', expiry.length]])}

  ${includeFinance && fin ? `
  <h2 class="sec">${sec()}. Key Figures</h2>
  ${keyFigures([
    { label: 'Cash on Hand', value: money(fin.summary?.cash_balance) },
    { label: 'Owner Capital', value: money(fin.summary?.capital_balance) },
    { label: 'Total Debit', value: money(debit) },
    { label: 'Total Credit', value: money(credit) },
    { label: 'Debit - Credit', value: signed(diff), tone: balanced ? 'pos' : 'neg' },
    { label: 'Journal Lines', value: money(journal.length) },
    { label: 'Categories', value: money(categories.length) },
    { label: 'Audit Notices', value: money(alerts.length), tone: alerts.length ? 'neg' : 'pos' },
  ])}

  ${alerts.length ? `
  <h2 class="sec">${sec()}. Audit Notices &amp; Variances</h2>
  ${table([{ label: '#', align: 'c' }, { label: 'Notice' }, { label: 'Action', align: 'c' }],
    alerts.map((a, i) => [i + 1, `${esc(a.message)}${a.arabic ? `<div class="note" dir="rtl">${esc(a.arabic)}</div>` : ''}`, '<span class="badge b-bad">Required</span>']))}` : ''}

  <h2 class="sec">${sec()}. Category Summary</h2>
  ${table(
    [{ label: 'Category' }, { label: 'Type' }, { label: 'Lines', align: 'r', num: true }, { label: 'Debit (CFA)', align: 'r', num: true }, { label: 'Credit (CFA)', align: 'r', num: true }],
    categories.map(([name, c]) => [esc(name), esc(c.type || ''), money((c.items || []).length), money(c.subtotal_debit), money(c.subtotal_credit)]),
    { foot: ['TOTAL', '', money(journal.length), money(debit), money(credit)], empty: 'No categorized entries.' }
  )}

  <h2 class="sec">${sec()}. Journal Entries</h2>
  ${table(
    [{ label: 'Date' }, { label: 'Account' }, { label: 'Description' }, { label: 'Debit (CFA)', align: 'r', num: true }, { label: 'Credit (CFA)', align: 'r', num: true }],
    journal.map((j) => [dateGB(j.date), esc(j.account_name), esc(j.entry_description || ''), num(j.debit) > 0 ? money(j.debit) : '—', num(j.credit) > 0 ? money(j.credit) : '—']),
    { foot: ['', '', 'GRAND TOTAL', money(debit), money(credit)], empty: 'No journal entries in this period.' }
  )}` : ''}

  <h2 class="sec">${sec()}. Stock Expiry Alerts (next ${esc(expiryDays)} days)</h2>
  ${table(
    [{ label: '#', align: 'c' }, { label: 'Product' }, { label: 'Branch' }, { label: 'SKU / Barcode' }, { label: 'Qty', align: 'r', num: true }, { label: 'Expiry Date' }, { label: 'Days Left', align: 'r', num: true }, { label: 'Status', align: 'c' }],
    expiry.map((it, i) => {
      const d = parseInt(it.days_until_expiry, 10);
      const [lbl, cls] = urgency(d);
      return [i + 1, esc(it.product_name), esc(it.depo_name || '—'), `${esc(it.sku || '—')}<div class="note">${esc(it.barcode || '')}</div>`, `${money(it.quantity)} ${esc(it.unit || '')}`, dateGB(it.expiry_date), d, `<span class="badge ${cls}">${lbl}</span>`];
    }),
    { empty: `No products expiring within ${expiryDays} days.` }
  )}

  <div class="declaration avoid">
    ${includeFinance
      ? `The ledger for the audited period is <b>${balanced ? 'balanced (total debit = total credit)' : `out of balance by ${money(Math.abs(diff))} CFA`}</b>.
         ${alerts.length ? `${alerts.length} audit notice(s) require follow-up.` : 'No audit notices were raised.'}`
      : 'Expired and critical items must be removed from sale and recorded as wastes.'}
    ${expiry.length ? ` ${expiry.filter((i) => parseInt(i.days_until_expiry, 10) <= 7).length} product line(s) are expired or expire within 7 days.` : ''}
  </div>

  ${signatures([['Prepared by (Accountant)', preparedBy], ['Reviewed by (Auditor)'], ['Approved by (Management)']])}`;

  openPrint({ settings, title: includeFinance ? `Financial Audit ${startDate} to ${endDate}` : 'Expiry Alerts', reference, body });
}
